export enum UserRole {
  PlatformAdmin = 'platform_admin',
  PlatformUser = 'platform_user',
  AppAdmin = 'app_admin',
  AppUser = 'app_user',
  ManageTap = 'manage_tap',
  TapApprover = 'tap_approver',
  // Epic #281 — Skills Management. Mirror backend app/auth/roles.py.
  SmAdmin = 'sm_admin',
  SmManager = 'sm_manager',
  // Feature #359 — People & Culture officer (SM > Coverage, read-only).
  PncOfficer = 'pnc_officer',
}

export interface AuthUser {
  id: string;
  entraOid: string;
  displayName: string;
  email: string;
  jobTitle?: string;
  department?: string;
  profilePhotoUrl?: string;
  entraProfileUrl?: string;
  lastSyncedAt?: string;
  roles: UserRole[];
  ouMemberships: string[];
}

export interface User {
  id: string;
  entraOid: string;
  displayName: string;
  email: string;
  jobTitle?: string;
  department?: string;
  profilePhotoUrl?: string;
  entraProfileUrl?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lastSyncedAt?: string;
  lastLoginAt?: string;
  ouMemberships: string[];
}

export interface Company {
  id: string;
  name: string;
  domain?: string;
  sourceSystem?: string;
  sourceId?: string;
  sourceUrl?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface Project {
  id: string;
  name: string;
  description?: string;
  clientId?: string;
  clientName?: string;
  status: string;
  sourceSystem?: string;
  sourceId?: string;
  sourceUrl?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface Notification {
  id: string;
  sourceApp: string;
  title: string;
  message: string;
  linkUrl?: string;
  isRead: boolean;
  createdAt: string;
  readAt?: string;
}

export interface AuditLog {
  id: string;
  action: string;
  entityType: string;
  entityId?: string;
  userId?: string;
  userName?: string;
  correlationId: string;
  beforeState?: Record<string, unknown>;
  afterState?: Record<string, unknown>;
  ipAddress?: string;
  createdAt: string;
}

// Feature #384 (Epic #381): a registered platform module and whether it is
// switched on in this environment. `key` matches `NavItem.appName`.
export interface PlatformApp {
  id: string;
  key: string;
  name: string;
  description?: string;
  isEnabled: boolean;
  isToggleable: boolean;
  updatedAt: string;
}

export interface NavItem {
  id: string;
  appName: string;
  label: string;
  icon?: string;
  path: string;
  parentId?: string;
  sortOrder: number;
  requiredOus: string[];
  // Migration 030 moved nav gating from OUs to roles; the backend still
  // emits both, so both are carried (WI #323).
  requiredRoles: string[];
  isVisible: boolean;
  children?: NavItem[];
}

export interface Setting {
  id: string;
  key: string;
  value: string;
  description?: string;
  updatedBy?: string;
  updatedAt: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  pages: number;
}

// WI #323: `/search` returns a flat per-hit projection, NOT full User /
// Company / Project entities. Declaring it as the latter is what made the
// search page read `displayName` off a payload that only ever carried
// `name`, and render user results blank. `api/search.ts` maps the
// snake_case wire shape onto this.
export interface SearchHit {
  id: string;
  type: string;
  name: string;
  // Users
  email?: string;
  department?: string;
  // Companies
  domain?: string;
  sourceSystem?: string;
  // Projects
  description?: string;
  clientId?: string;
}

export interface SearchResults {
  users: SearchHit[];
  companies: SearchHit[];
  projects: SearchHit[];
  query: string;
  total: number;
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  isActive?: boolean;
  // WI #337: some list endpoints (users, access groups, the SM catalogue)
  // filter to active rows by default and need an explicit opt-in to see the
  // rest. Mapped to `include_inactive` by `toRawListParams`.
  includeInactive?: boolean;
}

// WI #337: `GET /projects` also binds `client_id` and `status`. They are
// declared here (rather than passed as a loose object) so the mapper in
// `api/projects.ts` is the only place that has to know their wire names.
export interface ProjectListParams extends ListParams {
  clientId?: string;
  status?: string;
}

// --- Talent Acquisition Platform Types ---

export type TapStage = 'Review' | 'PhoneScreen' | 'Interview' | 'Assessment' | 'ReferenceCheck' | 'Offer' | 'Outcome'
export type TapDecisionOutcome = 'Successful' | 'Unsuccessful' | 'TalentPool' | 'NotRequired'
// Feature #223: outcome values are also exposed as Status Flags so a
// recruiter can flag an application's outcome alongside the existing
// operational flags (Urgent / OnHold / Withdrawn / Archived).
export type TapStatusFlag = 'Urgent' | 'OnHold' | 'Withdrawn' | 'Archived' | 'Successful' | 'Unsuccessful' | 'TalentPool'
export type TapApplicationType = 'JobApplication' | 'ExpressionOfInterest'
export type TapSlaStatus = 'Green' | 'Orange' | 'Red'

// Feature #148: lightweight applicant summary embedded in the application
// response so the person-first list + detail views can render a full name
// without a secondary fetch.
export interface TapApplicantSummary {
  id: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  // Feature #216: location shown on application summary.
  location?: string
}

export interface TapApplication {
  id: string
  referenceNumber: string
  applicationType: TapApplicationType
  positionTitle?: string
  vacancyCode?: string
  stage: TapStage
  decisionOutcome?: TapDecisionOutcome
  statusFlag?: TapStatusFlag
  assignedTo?: string
  // Bug #168: server-resolved display name for the assignee.
  assignedToDisplayName?: string
  receivedAt?: string
  summary?: string
  preferredEmploymentType?: string
  // Feature #217: availability hours.
  availability?: string
  sourceSystem: string
  sourceId: string
  slaStatus?: TapSlaStatus | null
  isActive: boolean
  createdAt: string
  updatedAt: string
  createdBy?: string
  // Feature #148: nested applicant summary. Optional because legacy records
  // may predate the applicant table being populated.
  applicant?: TapApplicantSummary
}

export interface TapApplicant {
  id: string
  applicationId: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  location?: string
  linkedinUrl?: string
  rightToWork?: string
  howHeard?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface TapSkill {
  id: string
  name: string
  description?: string
  isActive: boolean
}

// Feature #153: proficiency levels per Requirements §3.6.1.
export type TapSkillProficiency = 'Novice' | 'Capable' | 'Proficient' | 'Expert'
export const TAP_SKILL_PROFICIENCIES: TapSkillProficiency[] = [
  'Novice',
  'Capable',
  'Proficient',
  'Expert',
]

export interface TapApplicationSkill {
  id: string
  applicationId: string
  skillId: string
  // skillName is joined from tap_skill by the backend so the UI doesn't need
  // a second round-trip to render a list row.
  skillName?: string
  proficiency?: TapSkillProficiency | string
  isActive: boolean
  createdAt: string
}

// Feature #218: certifications attached to an application.
export interface TapApplicationCertification {
  id: string
  applicationId: string
  certificationName: string
  issuingAuthority?: string
  isActive: boolean
  createdAt: string
}

// Feature #218: generic lookup value used for typeahead suggestions.
export interface LabLookupValue {
  id: string
  category: string
  value: string
  description?: string
  sortOrder?: number
  isActive: boolean
}

export interface TapDocument {
  id: string
  applicationId: string
  kind: string
  filename: string
  contentType?: string
  sizeBytes?: number
  storageUrl: string
  version: number
  uploadedBy?: string
  uploadedAt?: string
  isActive: boolean
}

// Feature #162: outbound/inbound email on an application, used by the Emails tab.
export interface TapEmailMessage {
  id: string
  applicationId?: string
  threadId?: string
  messageId?: string
  direction?: string
  fromAddress?: string
  toAddresses?: string
  ccAddresses?: string
  subject?: string
  bodyHtml?: string
  bodyPreview?: string
  sentAt?: string
  hasAttachments?: boolean
  status?: string
  sentByUserId?: string
  // Resolved display name of the sender so the list view doesn't show a UUID.
  sentByDisplayName?: string
  templateKey?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

// Feature #162: platform-level email templates the compose modal picks from.
export interface TapEmailTemplate {
  id: string
  key: string
  name: string
  subject: string
  bodyHtml: string
  availableMergeFields?: string[]
  isActive: boolean
}

export interface TapWorkflowEvent {
  id: string
  applicationId: string
  eventType: string
  fromStage?: string
  toStage?: string
  fromFlag?: string
  toFlag?: string
  note?: string
  actorUserId?: string
  // Bug #152: resolved display name of the user who triggered the event so
  // the timeline doesn't have to show raw UUIDs.
  actorDisplayName?: string
  occurredAt: string
}

export interface TapNote {
  id: string
  applicationId: string
  body: string
  authorUserId?: string
  // Bug #164: author's resolved display name so the Notes tab can show a
  // human name instead of the raw Entra OID / lab_user UUID.
  authorDisplayName?: string
  stageWhenAdded: string
  isActive: boolean
  createdAt: string
}

export interface TapPhoneScreen {
  id: string
  applicationId: string
  scheduledAt?: string
  durationMinutes?: number
  conductedByUserId?: string
  locationOrLink?: string
  preparationNotes?: string
  outcomeSummary?: string
  recommendation?: string
  cancelledAt?: string
  isActive: boolean
  createdAt: string
}

// Feature #214: attendee assigned to an interview.
export interface TapInterviewAttendee {
  id: string
  userId: string
  displayName?: string
  isActive: boolean
}

export interface TapInterview {
  id: string
  applicationId: string
  interviewType: string
  scheduledAt?: string
  durationMinutes?: number
  locationOrLink?: string
  preparationNotes?: string
  scorecardTemplateId?: string
  cancelledAt?: string
  isActive: boolean
  createdAt: string
  attendees?: TapInterviewAttendee[]
}

// Feature #200: resource links attached to interviews.
export interface TapInterviewResourceLink {
  id: string
  interviewId: string
  label: string
  url: string
  isActive: boolean
  createdAt: string
}

export interface TapInterviewFeedback {
  id: string
  interviewId: string
  interviewerUserId?: string
  recommendation?: string
  score?: number
  feedback?: string
  isActive: boolean
  createdAt: string
  // Feature #177: scoring fields, present only when the feedback came from
  // the Score Interview flow.
  totalScore?: number
  scorecardTemplateId?: string
  responses?: { competencyId: string; score: number; comment?: string }[]
}

// Feature #229: per-column (Proposed or Max) computed rate set.
// Per the #229 corrective comment the Assessment tab displays per-hour
// (uniform across employment types) plus per-day as an optional secondary.
// Annual figures are no longer surfaced.
export interface TapCalculatedRateSet {
  costPerHour?: number
  buyPerHour?: number
  sellPerHour?: number
  costPerDay?: number
  buyPerDay?: number
  sellPerDay?: number
}

export interface TapAssessment {
  id: string
  applicationId: string
  roleSuitability?: string
  determinedSalaryMin?: number
  determinedSalaryMax?: number
  salaryCurrency?: string
  salaryUnit?: string
  determinedEmploymentType?: string
  lineManagerUserId?: string
  debriefNotes?: string
  buyRate?: number
  costRate?: number
  sellRate?: number
  // Feature #194: proposed/max rate split.
  rateProposed?: number
  buyRateProposed?: number
  buyRateMax?: number
  costRateProposed?: number
  costRateMax?: number
  sellRateProposed?: number
  sellRateMax?: number
  approvalStatus?: string
  approverUserId?: string
  // Bug #215: resolved display names from backend.
  lineManagerDisplayName?: string
  approverDisplayName?: string
  // Feature #229: live-computed rate bundle. Absent → no active calculator
  // settings or unrecognised employment type.
  calculatedRates?: {
    employmentTypeCanonical?: 'FullTime' | 'PartTime' | 'Casual' | 'Contractor'
    proposed?: TapCalculatedRateSet
    max?: TapCalculatedRateSet
    settingsId?: string
    settingsFyLabel?: string
  }
  isActive: boolean
  createdAt: string
}

export interface TapReferenceCheck {
  id: string
  applicationId: string
  refereeName: string
  refereeRelationship?: string
  refereeOrganisation?: string
  refereeJobTitle?: string
  refereeEmail?: string
  contactMethod?: string
  feedback?: string
  outcome?: string
  isActive: boolean
  createdAt: string
  // Feature #219: Typeform invite + response state. ``sent`` is set by
  // POST .../send-typeform; ``completed`` is set by the inbound webhook
  // when the referee submits the form.
  typeformSentAt?: string
  typeformCompletedAt?: string
  typeformResponseId?: string
}

export interface TapOffer {
  id: string
  applicationId: string
  verbalOfferResult?: string
  salary?: number
  salaryUnit?: string
  employmentType?: string
  location?: string
  approvalStatus?: string
  isActive: boolean
  createdAt: string
}

export interface TapAccessGrant {
  id: string
  applicationId: string
  userId: string
  grantType: string
  grantedByUserId: string
  grantedAt: string
  expiresAt?: string
  revokedAt?: string
  reason?: string
  isActive: boolean
}

export interface TapStageSla {
  id: string
  stage: string
  warningDays: number
  breachDays: number
  isEnabled: boolean
}

export interface TapScorecardTemplate {
  id: string
  name: string
  description?: string
  isActive: boolean
}

export interface TapScorecardCompetency {
  id: string
  templateId: string
  name: string
  description?: string
  weight: number
  sortOrder: number
  isActive: boolean
}

export interface TapDashboard {
  totalApplications: number
  byStage: Record<string, number>
  byVacancyCode: Record<string, number>
  avgTimeInStage: Record<string, number>
  conversionRates: Record<string, number>
  sourceBreakdown: Record<string, number>
  avgTimeToHire?: number
}

export interface TapApplicationListParams extends ListParams {
  stage?: TapStage
  decisionOutcome?: TapDecisionOutcome
  applicationType?: TapApplicationType
  vacancyCode?: string
  statusFlag?: TapStatusFlag
  assignedTo?: string
  dateFrom?: string
  dateTo?: string
  sharedWithMe?: boolean
}
